import { useState } from 'react';
import RegionTree from '../components/territories/admin/RegionTree';
import RegionForm from '../components/territories/admin/RegionForm';
import TerritoryEditor from '../components/territories/admin/TerritoryEditor';
import TerritoryForm from '../components/territories/admin/TerritoryForm';
import NamedAccountsTable from '../components/territories/admin/NamedAccountsTable';
import NamedAccountForm from '../components/territories/admin/NamedAccountForm';
import AssignmentRulesEditor from '../components/territories/admin/AssignmentRulesEditor';
import RealignmentWizard from '../components/territories/admin/RealignmentWizard';

const TABS = [
  { id: 'regions', label: 'Regions & Territories' },
  { id: 'named-accounts', label: 'Named Accounts' },
  { id: 'rules', label: 'Assignment Rules' },
  { id: 'realignment', label: 'Realignment' },
];

type RightPanel =
  | { type: 'none' }
  | { type: 'region-form'; regionId?: string }
  | { type: 'territory-editor'; territoryId: string }
  | { type: 'territory-form'; regionId?: string };

export default function TerritoryAdmin() {
  const [activeTab, setActiveTab] = useState('regions');
  const [selectedRegionId, setSelectedRegionId] = useState<string | undefined>();
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | undefined>();
  const [rightPanel, setRightPanel] = useState<RightPanel>({ type: 'none' });
  const [showNamedAccountForm, setShowNamedAccountForm] = useState(false);

  const handleSelectRegion = (regionId: string) => {
    setSelectedRegionId(regionId);
    setSelectedTerritoryId(undefined);
    setRightPanel({ type: 'region-form', regionId });
  };

  const handleSelectTerritory = (territoryId: string) => {
    setSelectedTerritoryId(territoryId);
    setSelectedRegionId(undefined);
    setRightPanel({ type: 'territory-editor', territoryId });
  };

  const handleAddRegion = () => {
    setSelectedRegionId(undefined);
    setSelectedTerritoryId(undefined);
    setRightPanel({ type: 'region-form' });
  };

  const handleAddTerritory = (regionId?: string) => {
    setSelectedRegionId(undefined);
    setSelectedTerritoryId(undefined);
    setRightPanel({ type: 'territory-form', regionId });
  };

  const handleClosePanel = () => {
    setRightPanel({ type: 'none' });
    setSelectedRegionId(undefined);
    setSelectedTerritoryId(undefined);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Territory Admin</h1>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex -mb-px space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'regions' && (
        <div className="flex gap-6 items-start">
          {/* Left panel — tree */}
          <div className="w-1/3 flex-shrink-0">
            <RegionTree
              onSelectRegion={handleSelectRegion}
              onSelectTerritory={handleSelectTerritory}
              onAddRegion={handleAddRegion}
              onAddTerritory={handleAddTerritory}
              selectedRegionId={selectedRegionId}
              selectedTerritoryId={selectedTerritoryId}
            />
          </div>

          {/* Right panel — editor / form */}
          <div className="flex-1">
            {rightPanel.type === 'none' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-gray-400 dark:text-gray-500">
                <p className="text-sm">Select a region or territory to edit, or use the buttons above to create one.</p>
              </div>
            )}

            {rightPanel.type === 'region-form' && (
              <RegionForm
                regionId={rightPanel.regionId}
                onClose={handleClosePanel}
              />
            )}

            {rightPanel.type === 'territory-editor' && (
              <TerritoryEditor territoryId={rightPanel.territoryId} />
            )}

            {rightPanel.type === 'territory-form' && (
              <TerritoryForm
                regionId={rightPanel.regionId}
                onClose={handleClosePanel}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'named-accounts' && (
        <>
          <NamedAccountsTable onAdd={() => setShowNamedAccountForm(true)} />
          {showNamedAccountForm && (
            <NamedAccountForm onClose={() => setShowNamedAccountForm(false)} />
          )}
        </>
      )}

      {activeTab === 'rules' && <AssignmentRulesEditor />}

      {activeTab === 'realignment' && <RealignmentWizard />}
    </div>
  );
}
